// WHAT YOU C(OPY) IS WHAT YOU P(ASTE)
import $ from 'jquery';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {parseTextWithLocale} from 'notebook/mediocre/clipboard/parseTextWithLocale';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Optional} from 'util/Types';

import {WYCIWYPPart} from './WyciwypConstants';

const SPECIAL_NODE_UUID = uuidv4();

export interface ParseTextOptions {
  indentation?: number;
  allowLinks?: boolean;
  extraSanitizeText?: (value: string, el: HTMLElement) => string;
  getNodeName?: (nodeName: string) => string;
  trimLeadingWhitespace?: boolean;
  trimTrailingWhitespace?: boolean;
  handleNode?: (node: Node, options: ParseTextOptions) => WYCIWYPPart;
  combineFmtTexts?: (fmtTexts: Array<WYCIWYPPart>) => Array<WYCIWYPPart>;
  tableType?: Optional<string>;
  /**
   * Controls whether locale-aware parsing of dates and numbers is applied to pasted text.
   *
   * - Set to `false` to **enable** locale-aware parsing. Use this for table cell contexts
   *   (e.g. from `parseTable`) where pasted values should be converted to canonical format
   *   according to the user's locale.
   * - Leave unset (`undefined`) or set to `true` to **skip** locale-aware parsing. This is
   *   the safe default for text blocks, headers, titles, and any non-table paste context
   *   where the pasted text should be preserved exactly as written.
   */
  skipLocaleParsing?: boolean;
}

/**
 * Output from parsing an incoming element into Benchling WYCIWYPParts:
 * @typedef {Object} ParsedText
 * @property {Object} fmtTexts - an array of {text, formats, indentation, listType, ...add'l data}
 * @property {boolean} didGetParsedWithLocale - true if text has been date/number parsed according to the user's locale
 */

/**
 * Constructs the formatted text array for any textual part. Always returns a list with at least
 * one element, even if that element corresponds to an empty string.
 * @param  {HTMLElement} el
 * @param  {Object} options - Should include `indentation` and `listType`, may optionally include
 *                            other fields as documented within the implementation.
 * @return {ParsedText} a parsed representation of the text that we can use in WYCIWYP
 */
export default function parseText(
  el: HTMLElement,
  options: ParseTextOptions
): {fmtTexts: Array<WYCIWYPPart>; didGetParsedWithLocale: boolean} {
  let nextSpecialNodeIndex;
  const specialNodeNames = ['BR'];
  let didGetParsedWithLocale = false;

  // Contains a map from '<SPECIAL_NODE_UUID>-<counter>*' to node, which is used to retrieve
  // information about the node.
  const mapping: {[uuid: string]: Node} = {};
  sanitizeText(el, specialNodeNames, mapping, options);
  const $el = $(el);

  const doWhiteSpaceProcessing =
    el.style != null && ['pre-wrap', 'pre-line', 'pre'].includes($el.css('whiteSpace'));
  let {text, formats} = formatters.getFormattedTextFromRenderedElement(el, 0, false, true, {
    getNodeName: options.getNodeName,
  });
  if (!options.allowLinks) {
    formats.link = [];
  }
  if (options.trimLeadingWhitespace) {
    ({text, formats} = formatters.trimLeft(text, formats));
  }
  if (options.trimTrailingWhitespace) {
    ({text, formats} = formatters.trimRight(text, formats));
  }

  // If pre is set, we want to just return the text and formats as is.
  if (doWhiteSpaceProcessing) {
    return {
      fmtTexts: [{text, formats, indentation: options.indentation, listType: null}],
      didGetParsedWithLocale,
    };
  }

  if (BenchlingConfig.ELN_ENABLE_LOCALE_AWARE_PARSING_FOR_COPY_PASTE && options.skipLocaleParsing === false) {
    const localeParsedText = parseTextWithLocale(text, options.tableType);
    if (text !== localeParsedText) {
      didGetParsedWithLocale = true;
      text = localeParsedText;
    }
  }

  // Special nodes have been replaced by UUIDs, which we now split on to create new noteparts.
  // Each newline should be its own notepart, with its own formatting.
  let fmtTexts = [];
  while (text.indexOf(SPECIAL_NODE_UUID) !== -1) {
    nextSpecialNodeIndex = text.indexOf(SPECIAL_NODE_UUID);
    const {before: beforeFormats, after: middleAfterFormats} = formatters.splitFormats(
      formats,
      nextSpecialNodeIndex
    );
    fmtTexts.push({
      text: text.slice(0, nextSpecialNodeIndex),
      formats: beforeFormats,
      indentation: options.indentation,
      listType: null,
    });

    const afterText = text.slice(nextSpecialNodeIndex);
    const newTextIndex = afterText.indexOf('*') + 1;
    assert(newTextIndex !== 0, 'Expected more "*"s to follow nextSpecialNodeIndex');

    const augmentedUUID = afterText.slice(0, newTextIndex);

    // `options.handleNode` is a function that can be passed in that will take in a node and generate
    // custom results. If no results are returned, normal operations will continue.
    if (options.handleNode != null) {
      const specialEl = mapping[augmentedUUID];
      const newFmtText = options.handleNode(specialEl, options);
      if (newFmtText != null) {
        fmtTexts.push(newFmtText);
      }
    }

    const {after: afterFormats} = formatters.splitFormats(middleAfterFormats, augmentedUUID.length);

    text = afterText.slice(newTextIndex);
    formats = afterFormats;
  }

  // Only push the last element if it's not empty, or if the array has no other elements.
  // This check guarantees that the array has at least one element.
  if (text !== '' || fmtTexts.length === 0) {
    fmtTexts.push({text, formats, indentation: options.indentation, listType: null});
  }

  // The `combineFmtTexts` flag, if set, is a function that combines fmtTexts as desired and
  // returns a new list of fmtTexts.
  // This is currently being used by the ENMLTranslator to combine results.
  if (options.combineFmtTexts != null) {
    fmtTexts = options.combineFmtTexts(fmtTexts);
  }

  assert(fmtTexts.length !== 0, 'Expected to parse some text');
  return {fmtTexts, didGetParsedWithLocale};
}

interface SanitizeTextOptions {
  extraSanitizeText?: (value: string, el: HTMLElement) => string;
  getNodeName?: (nodeName: string) => string;
}

function sanitizeText(
  el: HTMLElement,
  specialNodeNames: Array<string>,
  mapping: {[uuid: string]: Node},
  options: SanitizeTextOptions
): void {
  // If a node does not have white-space pre, adjacent whitespace will collapse into a single space when
  // rendered. However, the output of WYCIWYP reflects what is seen, so we must account for the white-space
  // during processing (esp. since the output will be used in a contentEditable with whitespace: pre)
  // $.text() does not take this into account, so we manually process the whitespace in a node.
  // CHildren nodes are recursively sanitized.

  if (options == null) {
    options = {};
  }
  if (el.nodeType === Node.TEXT_NODE) {
    const extraSanitizeText = options.extraSanitizeText ?? ((value: string) => value);
    el.nodeValue = extraSanitizeText(el.nodeValue!.replace(/\s+/g, ' '), el);
    return;
  }

  if (el.style != null && ['pre-wrap', 'pre-line', 'pre'].includes($(el).css('whiteSpace'))) {
    return;
  }

  // Recurse if the node does not have white-space pre
  $(el)
    .contents()
    .each(function (): void {
      // @ts-expect-error
      sanitizeText(this, specialNodeNames, mapping, options);
    });

  // Special characters (i.e. nodes that are inline but should still create new note parts such as
  // <br>) are replaced with '<SPECIAL_NODE_UUID>-<counter>*', which can be parsed out later after
  // getting the text from an inline node.
  // This is somewhat hacky solution which should be replaced with actually traversing the tree
  // and breaking down individual note parts that way.
  const $el = $(el);
  $el
    .contents()
    .filter(function (): boolean {
      const optionNodeName =
        // @ts-expect-error
        typeof options.getNodeName === 'function' ? options.getNodeName(this.nodeName) : undefined;
      // @ts-expect-error
      const nodeName = optionNodeName != null ? optionNodeName : this.nodeName;
      return Array.from(specialNodeNames).includes(nodeName);
    })
    .replaceWith(
      (function (): string {
        const augmentedUUID = `${SPECIAL_NODE_UUID}-${Object.keys(mapping).length}*`;
        // @ts-expect-error
        mapping[augmentedUUID] = this;
        return augmentedUUID;
      })()
    );
}
